import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { SITE, SOCIALS } from '../mock';
import { useReveal } from '../hooks/useReveal';

const FAQS = [
  {
    q: 'What is the ASIANRISK Roobet leaderboard?',
    a: 'A monthly wager race for players using code ASIANRISK on Roobet. The more you wager (based on the counting rules below), the higher you climb. At the end of each month the top five players share a $500 total prize pool.',
  },
  {
    q: 'Who is eligible to participate?',
    a: 'Adults who are of legal gambling age in their jurisdiction and who play on Roobet under code ASIANRISK. Roobet\u2019s own terms and any regional restrictions always apply.',
  },
  {
    q: 'How does code ASIANRISK work?',
    a: 'Register on Roobet through the AsianRisk link with code ASIANRISK so your account is referred under AsianRisk and your qualifying wagers count toward this leaderboard.',
  },
  {
    q: 'Which wagers count?',
    a: 'Wagers are weighted by game RTP: games with RTP of 97% or lower count 100% of the wager; RTP between 97.01% and 98.99% counts 50%; and RTP of 99% and over counts 10%. Amounts shown here may therefore differ from your raw Roobet stats.',
  },
  {
    q: 'When does the monthly race end?',
    a: 'Each race runs for one calendar month and closes at 00:00 UTC on the 1st of the next month. The exact start and end dates and a countdown are shown above the leaderboard.',
  },
  {
    q: 'What are the prizes?',
    a: '1st \u2014 $225, 2nd \u2014 $125, 3rd \u2014 $85, 4th \u2014 $45, 5th \u2014 $20. That is $500 in total each month, shared by the top five. Each prize is shown next to the matching position on the leaderboard.',
  },
  {
    q: 'How and when are prizes paid?',
    a: 'After the race closes, eligible winners follow the steps in the Prize Payouts section on this page. For any prize question, contact the team on Discord.',
  },
  {
    q: 'Where can I get support?',
    a: 'For leaderboard or code questions, join our Discord. For gambling-related help, visit BeGambleAware.org or GamblingTherapy.org \u2014 please always play responsibly (18+/21+).',
  },
];

function Item({ item, open, onToggle, idx }) {
  return (
    <div className="ar-panel overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`faq-panel-${idx}`}
        id={`faq-btn-${idx}`}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4 hover:bg-[rgba(0,217,255,0.04)] transition-colors"
      >
        <span className="font-display font-bold text-base sm:text-lg">{item.q}</span>
        <span className="flex-shrink-0 text-cyan" aria-hidden="true">{open ? <Minus size={20} /> : <Plus size={20} />}</span>
      </button>
      <div id={`faq-panel-${idx}`} role="region" aria-labelledby={`faq-btn-${idx}`} aria-hidden={!open} data-testid={`faq-panel-${idx}`} style={{ maxHeight: open ? 400 : 0, visibility: open ? 'visible' : 'hidden' }} className="transition-[max-height] duration-300 ease-out overflow-hidden">
        <p className="px-5 sm:px-6 pb-5 text-sm sm:text-[15px] text-muted leading-relaxed">{item.a}</p>
      </div>
    </div>
  );
}

export default function FAQ() {
  useReveal();
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-16 sm:py-24 relative">
      <div className="ar-container">
        <div className="text-center mb-10 sm:mb-14 reveal">
          <p className="ar-eyebrow mb-3">FAQ</p>
          <h2 className="ar-heading text-3xl sm:text-4xl md:text-5xl mb-4">Frequently Asked <span className="text-cyan">Questions</span></h2>
          <p className="text-muted max-w-2xl mx-auto">Everything you need to know about the monthly Roobet leaderboard and code {SITE.code}.</p>
        </div>

        <div className="max-w-3xl mx-auto flex flex-col gap-3 reveal">
          {FAQS.map((f, i) => (
            <Item key={f.q} idx={i} item={f} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>

        <p className="text-center text-sm text-muted mt-8 reveal">
          Still have a question?{' '}
          <a href={SOCIALS.discord} target="_blank" rel="noopener noreferrer" className="text-cyan font-semibold hover:underline">Ask in our Discord</a>.
        </p>
      </div>
    </section>
  );
}
